<template>
    <div>
        <div class="flex justify-center mb-32 relative">
            <steps v-model="activeTab" :steps="tabs" container/>
        </div>

        <component
            :is="tabs[activeTab - 1].component + '-tab'"
            v-if="tabs[activeTab - 1] && tabs[activeTab - 1].component"
            :campaign="campaign"
            :saving="saving"
            :tab="activeTab"
            @close="close"
            @next="next"
            @save="saveCampaign"
        ></component>
    </div>
</template>

<script>
import {mapState, mapActions} from 'pinia'
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import PopupModal from './PopupModal.vue';
import SvgIcon from './SvgIcon.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import PopupModalConfirm from './PopupModalConfirm.vue';
import CreateCampaignTab from "./CreateCampaignTab";
import Details from './campaign-tabs/Details';
import Channels from './campaign-tabs/Channels';
import Review from './campaign-tabs/Review';
import * as campaigns from "../../services/CampaignService";
import {normalizeJson} from "../../../shared/helpers";
import Campaign from "../../models/Campaign";
import Payment from './campaign-tabs/Payment';
import Steps from "../Steps";
import PaidSubscription from "../../models/PaidSubscription";

export default {
    components: {
        Steps,
        CreateCampaignTab,
        'details-tab': Details,
        'channels-tab': Channels,
        'review-tab': Review,
        'payment-tab': Payment,
        'popup-modal': PopupModal,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
    },
    props: {
        campaign: Campaign
    },
    data: function () {
        return {
            activeTab: null,
            tabOrder: [
                'details',
                'channels',
                'review',
                'payment',
            ],
            closing: false,
            saving: false
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        title() {
            return this.$t('campaigns.editor.title')
        },
        maxStep() {
            if (!this.campaign)
                return 1;

            switch (this.campaign.step) {
                case "details":
                    return 1;
                case "channels":
                    return 2;
                case "review":
                    return 3;
                default:
                    return 4;
            }
        },
        tabs() {
            return [
                {
                    title: this.$t('campaigns.editor.steps.details'),
                    icon: 'description',
                    component: 'details',
                    hidden: this.campaign && this.campaign.limited
                },
                {
                    title: this.$t('campaigns.editor.steps.channels'),
                    icon: 'list-alt',
                    component: 'channels',
                    disabled: this.maxStep < 2
                },
                {
                    title: this.$t('campaigns.editor.steps.review'),
                    icon: 'conversion-path',
                    component: 'review',
                    disabled: this.maxStep < 3
                }
            ].filter(Boolean)
        },
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription;
        },
        multiPostingSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        close() {
            this.$router.push({name: 'campaigns'})
        },
        cancelClose() {
            this.closing = false;
        },
        next() {
            this.activeTab++;
            window.bus.dispatch('popup-top');
        },
        notify() {
            this[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t('general.changes_saved'),
            });

            this.close(false);
        },
        setActiveTab(tab) {
            this.activeTab = tab;
        },

        async saveCampaign({form, relationships, todo}) {
            this.saving = true;

            if (typeof relationships == 'undefined') {
                relationships = {};
            }

            try {
                if (this.campaign) {
                    await this.updateCampaign(form, relationships);
                } else {
                    await this.createCampaign(form, relationships);
                    this.saving = false;
                    this.$emit('saved');
                    return; // already redirected to different page.
                }

                if (todo) {
                    this[todo](false);
                    if (todo === 'close') {
                        this.$emit('success');
                    }
                }
            } catch (e) {
            }

            // Scroll to top
            document.getElementById('content-container').scrollTop = 0;
            this.saving = false;
            this.$emit('saved');
        },
        createCampaign(form, relationships) {
            return form.submit(data => campaigns.store({
                data: {
                    attributes: data,
                    relationships
                },
            })).then(result => {
                return useRepo(Campaign).save(normalizeJson(result));
            }).then(result => {
                this.$router.push({name: 'campaign', params: {id: result.id}})
            });
        },
        updateCampaign(form, relationships) {
            return form.submit(data => campaigns.update(this.campaign.id, {
                data: {
                    attributes: data,
                    relationships
                },
            })).then(result => {
                return useRepo(Campaign).save(normalizeJson(result));
            });
        },
    },
    beforeMount() {
        // Redirect to campaigns if user has multi-posting subscription
        if (this.hasMultiPostingSubscription && this.currentCompany.multiposting_status === 'active') {
            this.$router.push({ name: 'campaigns' });
        }
    },
    mounted() {
        // check if the url has a campaign id
        if (this.$route.query.campaign) {
            // get the campaign from the store
            this.campaign = useRepo(Campaign).find(this.$route.query.campaign);
            if (!this.campaign) {
                // if the campaign is not in the store, get it from the server
                campaigns.show(this.$route.query.campaign).then(result => {
                    this.campaign = useRepo(Campaign).save(normalizeJson(result));
                });
            }
        }
        this.activeTab = this.maxStep;
    }
}
</script>
